import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Boxes, Plus, Pencil, Archive, ArchiveRestore, Loader, AlertTriangle, Info,
} from 'lucide-react';
import { labImages as labImagesApi, labs as labsApi } from '../services/api';
import { Dialog, DialogHeader, DialogBody, DialogFooter, FormSection, Field, SwitchRow } from './Dialog';
import { useImageCatalog } from './ImagePicker';
import { formatMemory, orderEntries, shortImageRef, matchEntryForLab } from '../utils/imageCatalog';

/**
 * Admin catalogue manager — the closed set of environments faculty choose from.
 *
 * Three things drive the design:
 *
 *  - An entry is a container image that gets scheduled into the `labs`
 *    namespace against student volumes, so the form says what each field does
 *    to a running class rather than merely naming it.
 *  - `key` is the identity a lab stores. It is settable once, on creation, and
 *    read-only afterwards, because changing it would orphan every lab that
 *    already points at it.
 *  - Retiring is the only destructive action and its blast radius is "every lab
 *    still pointing at this". The usage count is shown before the confirmation,
 *    and an unknown count is treated as a non-zero one.
 */

const PRUNE_BIN_OPTIONS = [
  {
    value: 'always',
    label: 'Always delete bin/',
    help: 'Today\'s behaviour, correct for Java-only images: Eclipse rebuilds bin/ from source.',
  },
  {
    value: 'java-only',
    label: 'Only beside a Java project',
    help: 'Deletes bin/ only next to a .classpath / pom.xml / build.gradle. Safe for mixed images.',
  },
  {
    value: 'never',
    label: 'Never touch bin/',
    help: 'Required for Dart, Flutter and Go, where bin/ holds the program\'s entry point.',
  },
];

const iconBtnStyle = (color) => ({
  background: 'none',
  border: '1px solid var(--border-primary)',
  borderRadius: 6,
  color: color || 'var(--text-secondary)',
  cursor: 'pointer',
  padding: 6,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const monoStyle = {
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  color: 'var(--text-secondary)',
  wordBreak: 'break-all',
};

function Chip({ children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 4, marginRight: 4, marginBottom: 2,
      background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
      fontSize: 11, color: 'var(--text-secondary)',
    }}>
      {children}
    </span>
  );
}

function WarningLine({ children }) {
  return (
    <p role="status" style={{
      display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: -4, marginBottom: 14,
      fontSize: 12.5, color: 'var(--accent-warning)',
    }}>
      <AlertTriangle size={13} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
      <span>{children}</span>
    </p>
  );
}

/**
 * Image references that will not pull, that will drift, or that the server will
 * refuse outright. Said before submit rather than after a 400.
 */
export function imageRefWarnings(ref, allowedRegistries) {
  const value = String(ref || '').trim();
  if (!value) return [];
  const out = [];
  const firstSegment = value.split('/')[0];
  const hasRegistry = value.includes('/')
    && (firstSegment.includes('.') || firstSegment.includes(':') || firstSegment === 'localhost');

  if (!hasRegistry) {
    out.push('No registry host. Kubernetes resolves that against Docker Hub, so every pod for every lab using it would fail to pull. The server will reject this.');
  } else if (Array.isArray(allowedRegistries) && allowedRegistries.length > 0
    && !allowedRegistries.includes('*') && !allowedRegistries.includes(firstSegment)) {
    out.push(`"${firstSegment}" is not an allowed registry. The server accepts only: ${allowedRegistries.join(', ')}.`);
  }

  const nameAfterRegistry = hasRegistry ? value.slice(firstSegment.length + 1) : value;
  if (!nameAfterRegistry.includes(':') && !nameAfterRegistry.includes('@')) {
    out.push('No tag. The platform will pull :latest, which can change underneath a running course.');
  }
  return out;
}

function ImageCatalogManager() {
  const queryClient = useQueryClient();
  const [showRetired, setShowRetired] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [retireEntry, setRetireEntry] = useState(null);

  const { entries, available, isLoading, isError, unavailableReason, allowedRegistries } =
    useImageCatalog({ includeRetired: showRetired });

  // "How many labs use this" is the number that gates retirement, and the
  // catalogue route does not report it. It is derived here from the lab list,
  // resolved through exactly the same matcher the picker uses, so the count and
  // the selection a faculty member sees can never disagree. A failed or missing
  // lab list leaves the count null, which the retire dialog treats as "in use".
  const { data: labsData } = useQuery({
    queryKey: ['labs'],
    queryFn: () => labsApi.list(),
    retry: false,
    staleTime: 60 * 1000,
  });

  const usageByKey = useMemo(() => {
    if (!Array.isArray(labsData) || entries.length === 0) return null;
    const counts = new Map();
    for (const lab of labsData) {
      const entry = matchEntryForLab(entries, lab?.containerConfig);
      if (entry && !entry.unknown) counts.set(entry.key, (counts.get(entry.key) || 0) + 1);
    }
    return counts;
  }, [labsData, entries]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lab-images'] });
    queryClient.invalidateQueries({ queryKey: ['labs'] });
  };

  const rows = useMemo(() => {
    const visible = showRetired ? entries : entries.filter((e) => !e.retired);
    // Prefer a server-reported count if the route ever starts sending one.
    return orderEntries(visible).map((e) => ({
      ...e,
      labCount: e.labCount ?? (usageByKey ? (usageByKey.get(e.key) || 0) : null),
    }));
  }, [entries, showRetired, usageByKey]);

  const restoreMutation = useMutation({
    mutationFn: (entry) => labImagesApi.setRetired(entry.key, false),
    onSuccess: invalidate,
  });

  return (
    <div className="card" style={{ marginBottom: 24 }} data-testid="image-catalog-manager">
      <div className="card-header">
        <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Boxes size={20} aria-hidden="true" />
          Lab environments
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
              data-testid="image-catalog-show-retired"
            />
            Show retired
          </label>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowCreate(true)}
            disabled={isError}
            data-testid="image-catalog-add"
            style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: isError ? 0.5 : 1 }}
          >
            <Plus size={16} aria-hidden="true" /> Add environment
          </button>
        </div>
      </div>

      <p className="text-muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        What faculty can choose from when they set up a lab. Every entry is a container image
        that gets scheduled against student volumes, so the list is deliberately closed —
        faculty pick from it, they never type an image name.
      </p>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }} role="status" aria-label="Loading environments">
          <Loader size={28} className="spinner" aria-hidden="true" />
        </div>
      ) : isError ? (
        <div role="alert" style={{
          padding: '12px 16px', borderRadius: 8,
          background: 'var(--badge-warning-bg)', border: '1px solid var(--accent-warning)',
          color: 'var(--accent-warning)', fontSize: 13.5,
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <Info size={15} aria-hidden="true" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            {unavailableReason} Until it does, every lab runs the standard environment and the
            picker on the lab settings page is read-only.
          </span>
        </div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <Boxes size={40} style={{ marginBottom: 12, opacity: 0.3 }} aria-hidden="true" />
          <p style={{ fontSize: 15, marginBottom: 6 }}>No environments defined</p>
          <p style={{ fontSize: 13 }}>Add one to give faculty something to choose.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table" data-testid="image-catalog-table">
            <thead>
              <tr>
                <th>Environment</th>
                <th>Image</th>
                <th>Suggested for</th>
                <th>Memory</th>
                <th>Labs using</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.key} data-testid={`image-catalog-row-${entry.key}`}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {entry.name}
                      {entry.isDefault && (
                        <span style={{
                          marginLeft: 8, fontSize: 11, fontWeight: 600,
                          color: 'var(--accent-primary)', background: 'var(--badge-info-bg)',
                          padding: '1px 6px', borderRadius: 4,
                        }}>
                          Default
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      <code style={{ ...monoStyle, fontSize: 11 }}>{entry.key}</code>
                      {entry.description ? ` · ${entry.description}` : ''}
                    </div>
                  </td>
                  <td>
                    <code style={monoStyle}>{shortImageRef(entry.image) || '—'}</code>
                    {entry.resolvedImage && entry.resolvedImage !== entry.image && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        resolves to <code style={{ ...monoStyle, fontSize: 11 }}>{entry.resolvedImage}</code>
                      </div>
                    )}
                  </td>
                  <td style={{ maxWidth: 220 }}>
                    {entry.suggestedFor.length > 0
                      ? entry.suggestedFor.map((c) => <Chip key={c}>{c}</Chip>)
                      : <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {entry.suggestedMemory ? formatMemory(entry.suggestedMemory) : '—'}
                  </td>
                  <td style={{ fontSize: 13 }} data-testid={`image-catalog-usage-${entry.key}`}>
                    {entry.labCount === null
                      ? <span style={{ color: 'var(--text-muted)' }} title="The backend did not report usage for this entry">unknown</span>
                      : `${entry.labCount} lab${entry.labCount === 1 ? '' : 's'}`}
                  </td>
                  <td>
                    <span className={entry.retired ? 'badge badge-warning' : 'badge badge-success'}>
                      {entry.retired ? 'Retired' : 'Active'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button
                        title="Edit environment"
                        aria-label={`Edit ${entry.name}`}
                        data-testid={`image-catalog-edit-${entry.key}`}
                        onClick={() => setEditEntry(entry)}
                        style={iconBtnStyle()}
                      >
                        <Pencil size={15} aria-hidden="true" />
                      </button>
                      {entry.retired ? (
                        <button
                          title="Offer this environment to new labs again"
                          aria-label={`Restore ${entry.name}`}
                          data-testid={`image-catalog-restore-${entry.key}`}
                          onClick={() => restoreMutation.mutate(entry)}
                          disabled={restoreMutation.isPending}
                          style={iconBtnStyle('var(--accent-success)')}
                        >
                          <ArchiveRestore size={15} aria-hidden="true" />
                        </button>
                      ) : (
                        <button
                          title={entry.isDefault
                            ? 'The default environment cannot be retired — make another one the default first'
                            : 'Retire environment'}
                          aria-label={`Retire ${entry.name}`}
                          data-testid={`image-catalog-retire-${entry.key}`}
                          onClick={() => setRetireEntry(entry)}
                          disabled={entry.isDefault}
                          style={{
                            ...iconBtnStyle('var(--accent-warning)'),
                            opacity: entry.isDefault ? 0.4 : 1,
                            cursor: entry.isDefault ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <Archive size={15} aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {restoreMutation.isError && (
        <div role="alert" className="dlg-alert" style={{ marginTop: 12 }}>
          {restoreMutation.error?.response?.data?.message
            || restoreMutation.error?.response?.data?.error
            || 'Failed to restore that environment'}
        </div>
      )}

      {showCreate && (
        <EntryModal
          existingKeys={entries.map((e) => e.key)}
          allowedRegistries={allowedRegistries}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); invalidate(); }}
        />
      )}
      {editEntry && (
        <EntryModal
          entry={editEntry}
          existingKeys={entries.map((e) => e.key)}
          allowedRegistries={allowedRegistries}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); invalidate(); }}
        />
      )}
      {retireEntry && (
        <RetireModal
          entry={retireEntry}
          onClose={() => setRetireEntry(null)}
          onSaved={() => { setRetireEntry(null); invalidate(); }}
        />
      )}

      {!isLoading && !isError && available && (
        <p className="text-muted" style={{ fontSize: 12.5, marginTop: 16, marginBottom: 0 }}>
          Changes here apply to labs from the moment a faculty member picks them. No student
          environment changes until it is recreated, and Recreate All skips anything running.
        </p>
      )}
    </div>
  );
}

/* --------------------------- Create / edit modal --------------------------- */
function EntryModal({ entry, existingKeys = [], allowedRegistries, onClose, onSaved }) {
  const isEdit = !!entry;
  const [key, setKey] = useState(entry?.key || '');
  const [displayName, setDisplayName] = useState(entry?.name || '');
  const [image, setImage] = useState(entry?.image || '');
  const [description, setDescription] = useState(entry?.description || '');
  const [toolchains, setToolchains] = useState((entry?.toolchains || []).join(', '));
  const [suggestedFor, setSuggestedFor] = useState((entry?.suggestedFor || []).join(', '));
  const [memory, setMemory] = useState(entry?.suggestedMemoryRequest || '');
  const [memoryLimit, setMemoryLimit] = useState(entry?.suggestedMemory || '');
  const [cpus, setCpus] = useState(entry?.suggestedCpu || '');
  const [cpuLimit, setCpuLimit] = useState(entry?.suggestedCpuLimit || '');
  const [normalizeJava, setNormalizeJava] = useState(entry?.starterCode?.normalizeJavaProject !== false);
  const [pruneBinDirs, setPruneBinDirs] = useState(entry?.starterCode?.pruneBinDirs || 'always');
  const [javaRelease, setJavaRelease] = useState(String(entry?.starterCode?.javaRelease || 21));
  const [sortOrder, setSortOrder] = useState(String(entry?.sortOrder ?? 100));
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (payload) => (isEdit
      ? labImagesApi.update(entry.key, payload)
      : labImagesApi.create(payload)),
    onSuccess: () => onSaved(),
    onError: (err) => setError(
      err.response?.data?.message
      || err.response?.data?.error
      || `Failed to ${isEdit ? 'update' : 'create'} environment`
    ),
  });

  const isPending = mutation.isPending;
  const warnings = imageRefWarnings(image, allowedRegistries);
  const keyTaken = !isEdit && existingKeys.includes(key.trim().toLowerCase());
  const pruneHelp = PRUNE_BIN_OPTIONS.find((o) => o.value === pruneBinDirs)?.help;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const trimmedKey = key.trim().toLowerCase();
    if (!isEdit) {
      if (!trimmedKey) { setError('Key is required'); return; }
      if (!/^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/.test(trimmedKey)) {
        setError('Key must be 3–50 lowercase letters, digits or dashes, and cannot start or end with a dash.');
        return;
      }
      if (keyTaken) { setError('That key is already in use.'); return; }
    }
    if (!displayName.trim()) { setError('Name is required'); return; }
    if (!image.trim()) { setError('Image reference is required'); return; }

    const payload = {
      displayName: displayName.trim(),
      image: image.trim(),
      description: description.trim(),
      toolchains: toolchains.split(/[,\n]+/).map((t) => t.trim()).filter(Boolean),
      suggestedFor: suggestedFor.split(/[,\s]+/).map((c) => c.trim().toUpperCase()).filter(Boolean),
      suggestedResources: {
        memory: memory.trim() || null,
        memoryLimit: memoryLimit.trim() || null,
        cpus: cpus.trim() || null,
        cpuLimit: cpuLimit.trim() || null,
      },
      starterCode: {
        normalizeJavaProject: normalizeJava,
        pruneBinDirs,
        javaRelease: parseInt(javaRelease, 10) || 21,
      },
      sortOrder: parseInt(sortOrder, 10) || 100,
    };
    // The key is the identity every lab stores. Send it only on create.
    if (!isEdit) payload.key = trimmedKey;

    mutation.mutate(payload);
  };

  return (
    <Dialog size="lg" onClose={isPending ? undefined : onClose} labelledBy="image-entry-title">
      <DialogHeader
        id="image-entry-title"
        title={isEdit ? `Edit ${entry.name}` : 'Add environment'}
        icon={<Boxes size={20} aria-hidden="true" />}
        onClose={isPending ? undefined : onClose}
      />
      <form className="dlg-form" onSubmit={handleSubmit} aria-label={isEdit ? 'Edit environment' : 'Add environment'}>
        <DialogBody>
          {error && <div role="alert" className="dlg-alert">{error}</div>}

          <FormSection title="Identity">
            <div className="form-grid form-grid-21">
              <Field
                label="Name"
                required
                hint="What faculty see in the picker. Name the toolchain, not the image."
              >
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Flutter and Spring Boot"
                  data-testid="image-entry-name"
                  required
                />
              </Field>
              <Field
                label="Key"
                required={!isEdit}
                hint={isEdit
                  ? 'Fixed. Labs store this, so changing it would orphan them.'
                  : 'Lowercase slug. Permanent once labs start using it.'}
              >
                <input
                  type="text"
                  value={key}
                  onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="infs4102"
                  data-testid="image-entry-key"
                  disabled={isEdit}
                  required={!isEdit}
                  spellCheck={false}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 13, opacity: isEdit ? 0.6 : 1 }}
                />
              </Field>
            </div>

            {keyTaken && <WarningLine>Another environment already uses that key.</WarningLine>}

            <Field
              label="Image reference"
              required
              hint="Fully qualified, including the registry host and an explicit tag."
            >
              <input
                type="text"
                value={image}
                onChange={(e) => setImage(e.target.value)}
                placeholder="labenvacr.azurecr.io/lab-environment-infs4102:v2026-01"
                data-testid="image-entry-image"
                spellCheck={false}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
                required
              />
            </Field>

            {warnings.map((w) => <WarningLine key={w}>{w}</WarningLine>)}

            <Field label="Description" hint="One line. What a student can actually do in it.">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Flutter desktop, Dart, JDK 21 and Maven."
                rows={2}
                data-testid="image-entry-description"
              />
            </Field>

            <div className="form-grid form-grid-2">
              <Field label="Toolchains" hint="Shown under the name, comma separated.">
                <input
                  type="text"
                  value={toolchains}
                  onChange={(e) => setToolchains(e.target.value)}
                  placeholder="Flutter, Dart, Java 21, Maven"
                  data-testid="image-entry-toolchains"
                />
              </Field>
              <Field
                label="Suggested for"
                hint="Course codes, comma separated. Preselects this environment on a new lab. A prefix such as INFS also matches."
              >
                <input
                  type="text"
                  value={suggestedFor}
                  onChange={(e) => setSuggestedFor(e.target.value)}
                  placeholder="INFS4102, INFS3201"
                  data-testid="image-entry-suggested-for"
                  style={{ textTransform: 'uppercase' }}
                />
              </Field>
            </div>
          </FormSection>

          <FormSection title="Suggested resources">
            <p className="field-hint" style={{ marginTop: -4 }}>
              Applied as the starting point when a lab picks this environment; faculty keep the
              per-lab override. A request is a reservation and costs capacity on a CPU-bound
              pool. A limit is a ceiling — over the memory limit the student's session is
              destroyed, over the CPU limit it merely runs slowly.
            </p>
            <div className="form-grid form-grid-2">
              <Field label="Memory request" hint="e.g. 4Gi. What the scheduler reserves.">
                <input
                  type="text" value={memory} onChange={(e) => setMemory(e.target.value)}
                  placeholder="4Gi" data-testid="image-entry-memory"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
                />
              </Field>
              <Field label="Memory limit" hint="e.g. 8Gi. Exceeding this ends the session.">
                <input
                  type="text" value={memoryLimit} onChange={(e) => setMemoryLimit(e.target.value)}
                  placeholder="8Gi" data-testid="image-entry-memory-limit"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
                />
              </Field>
            </div>
            <div className="form-grid form-grid-2">
              <Field label="CPU request" hint="e.g. 1. Raising this is what actually costs nodes.">
                <input
                  type="text" value={cpus} onChange={(e) => setCpus(e.target.value)}
                  placeholder="1" data-testid="image-entry-cpus"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
                />
              </Field>
              <Field label="CPU limit" hint="e.g. 4. A throttle, not a kill — safe to be generous.">
                <input
                  type="text" value={cpuLimit} onChange={(e) => setCpuLimit(e.target.value)}
                  placeholder="4" data-testid="image-entry-cpu-limit"
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
                />
              </Field>
            </div>
          </FormSection>

          <FormSection title="Starter code handling">
            <Field
              label="bin/ directories"
              htmlFor="image-entry-prune"
              hint={pruneHelp}
            >
              <select
                id="image-entry-prune"
                value={pruneBinDirs}
                onChange={(e) => setPruneBinDirs(e.target.value)}
                data-testid="image-entry-prune"
                style={{ width: '100%' }}
              >
                {PRUNE_BIN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>

            {pruneBinDirs === 'always' && (
              <WarningLine>
                Every bin/ directory in uploaded starter code is deleted. If this image carries
                Dart, Flutter or Go, that removes the program's entry point after an upload that
                reported success — choose one of the other two.
              </WarningLine>
            )}

            <SwitchRow
              id="image-entry-normalize-java"
              title="Normalise Java project metadata"
              description="Rewrites .classpath and JDT preferences to the JDK below and drops module-info.java. Harmless on a project with no Java in it."
              checked={normalizeJava}
              onChange={(e) => setNormalizeJava(e.target.checked)}
            />

            {normalizeJava && (
              <Field label="JDK release" hint="The Java version installed in this image.">
                <input
                  type="number"
                  min="8"
                  max="99"
                  value={javaRelease}
                  onChange={(e) => setJavaRelease(e.target.value)}
                  data-testid="image-entry-java-release"
                  style={{ width: 120, textAlign: 'center' }}
                />
              </Field>
            )}
          </FormSection>

          <FormSection title="Availability">
            <p className="field-hint" style={{ marginTop: -4, marginBottom: 14 }}>
              {entry?.isDefault
                ? 'This is the platform default: labs that never chose an environment run it.'
                : 'The platform default cannot be reassigned from this screen.'}{' '}
              The default entry carries the unqualified reference each cluster maps onto its own
              K8S_LAB_IMAGE, which is what keeps staging labs off production's tag, so it is
              fixed at deployment rather than edited here.
            </p>
            <Field label="Sort order" hint="Lower numbers appear first in the picker.">
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                data-testid="image-entry-sort-order"
                style={{ width: 120, textAlign: 'center' }}
              />
            </Field>
          </FormSection>
        </DialogBody>
        <DialogFooter>
          <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isPending}
            data-testid="image-entry-submit"
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {isPending ? <Loader size={16} className="spinner" aria-hidden="true" /> : <Plus size={16} aria-hidden="true" />}
            {isEdit ? 'Save changes' : 'Add environment'}
          </button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

/* ------------------------------ Retire modal ------------------------------ */
function RetireModal({ entry, onClose, onSaved }) {
  const [error, setError] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const mutation = useMutation({
    mutationFn: () => labImagesApi.setRetired(entry.key, true),
    onSuccess: () => onSaved(),
    onError: (err) => setError(
      err.response?.data?.message || err.response?.data?.error || 'Failed to retire environment'
    ),
  });

  const isPending = mutation.isPending;
  // An unknown count is treated exactly like a non-zero one. "The backend did not
  // tell me" is not evidence that nothing is using this.
  const inUse = entry.labCount === null || entry.labCount > 0;
  const canRetire = !inUse || acknowledged;

  return (
    <Dialog size="sm" onClose={isPending ? undefined : onClose} labelledBy="retire-image-title" confirmOnClose={false}>
      <DialogHeader
        id="retire-image-title"
        title="Retire environment"
        icon={<Archive size={18} aria-hidden="true" />}
        onClose={isPending ? undefined : onClose}
      />
      <DialogBody>
        {error && <div role="alert" className="dlg-alert">{error}</div>}

        <p style={{ marginTop: 0 }}>Retire <strong>{entry.name}</strong>?</p>
        <p style={{ ...monoStyle, marginTop: -6 }}>{shortImageRef(entry.image) || entry.key}</p>

        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 14,
          background: inUse ? 'var(--badge-warning-bg)' : 'var(--bg-tertiary)',
          border: `1px solid ${inUse ? 'var(--accent-warning)' : 'var(--border-primary)'}`,
          fontSize: 13.5,
        }} data-testid="retire-image-usage">
          {entry.labCount === null ? (
            <>Usage is <strong>unknown</strong> — the platform did not report how many labs point
              at this environment. Treat it as in use.</>
          ) : entry.labCount === 0 ? (
            <>No labs use this environment.</>
          ) : (
            <><strong>{entry.labCount} lab{entry.labCount === 1 ? '' : 's'}</strong> currently{' '}
              {entry.labCount === 1 ? 'points' : 'point'} at this environment.</>
          )}
        </div>

        <p className="text-muted" style={{ fontSize: 13 }}>
          Retiring removes it from the picker for new labs. It does not change what any existing
          lab runs and it does not touch a running environment, so nothing breaks mid-semester.
          Retiring is reversible.
        </p>

        {inUse && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, cursor: 'pointer', marginTop: 12 }}>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="retire-image-acknowledge"
              style={{ width: 16, height: 16, marginTop: 2 }}
            />
            <span>
              I understand those labs keep running this image, and that it will no longer be
              offered to new ones.
            </span>
          </label>
        )}
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => { setError(''); mutation.mutate(); }}
          disabled={isPending || !canRetire}
          data-testid="confirm-retire-image"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            opacity: canRetire ? 1 : 0.5,
            cursor: canRetire ? 'pointer' : 'not-allowed',
          }}
        >
          {isPending ? <Loader size={16} className="spinner" aria-hidden="true" /> : <Archive size={16} aria-hidden="true" />}
          Retire
        </button>
      </DialogFooter>
    </Dialog>
  );
}

export default ImageCatalogManager;
